import { useEffect, useMemo, useRef, useState } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { StatCard } from "@/components/ui/stat-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useBusiness } from "@/hooks/use-business";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import { Calendar as CalendarIcon, Download, ExternalLink, Loader2, Mail, Plus, Search, Send, Trash2, Banknote, FileText, CheckCircle2, Clock } from "lucide-react";
import { useLocation } from "react-router-dom";
import { getBaseUrl } from "@/lib/site";

type InvoiceRow = any;
type InvoiceItemRow = { description: string; quantity: string; unit_price: string };
type InvoiceAdjustmentRow = {
  type: "tax" | "discount";
  label: string;
  percent_rate: string; // e.g. "15"
  included_in_prices: boolean; // only meaningful for taxes (VAT-inclusive)
};

function money(currency: string, amount: number) {
  return `${currency} ${amount.toFixed(2)}`;
}

function computeAdjustmentAmount(a: InvoiceAdjustmentRow, subtotal: number) {
  const rate = Number(a.percent_rate || 0);

  if (a.type === "tax" && a.included_in_prices) {
    // VAT-inclusive: subtotal already includes tax. Extract tax portion.
    // Example: subtotal=115, rate=15 => tax=15
    return rate > 0 ? subtotal * (rate / (100 + rate)) : 0;
  }
  return subtotal * (rate / 100);
}

export default function Invoicing() {
  const { business, loading: businessLoading } = useBusiness();
  const { toast } = useToast();
  const sb = supabase as any;
  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [query, setQuery] = useState("");

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<InvoiceRow | null>(null);

  const [items, setItems] = useState<InvoiceItemRow[]>([{ description: "", quantity: "1", unit_price: "0" }]);
  const [adjustments, setAdjustments] = useState<InvoiceAdjustmentRow[]>([]);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerSearching, setCustomerSearching] = useState(false);
  const [customerResults, setCustomerResults] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);

  const [form, setForm] = useState({
    bill_to_name: "",
    bill_to_email: "",
    bill_to_phone: "",
    issue_date: format(new Date(), "yyyy-MM-dd"),
    due_date: "",
    payment_terms: "",
    notes: "",
    terms: "",
    status: "draft" as "draft" | "sent" | "paid" | "void",
  });

  const lastCreatedIdRef = useRef<string | null>(null);
  const handledEditParamRef = useRef<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter((i: any) => {
      return (
        String(i.invoice_number || "").toLowerCase().includes(q) ||
        String(i.bill_to_name || "").toLowerCase().includes(q) ||
        String(i.bill_to_email || "").toLowerCase().includes(q) ||
        String(i.status || "").toLowerCase().includes(q)
      );
    });
  }, [invoices, query]);

  const currency = (business as any)?.currency || "GHS";

  // Calculate invoice metrics
  const invoiceMetrics = useMemo(() => {
    const total = invoices.length;
    const paid = invoices.filter((i: any) => i.status === "paid").length;
    const pending = invoices.filter((i: any) => i.status === "sent" || i.status === "draft").length;
    const overdue = invoices.filter((i: any) => {
      if (i.status === "paid" || i.status === "void") return false;
      if (!i.due_date) return false;
      return new Date(i.due_date) < new Date();
    }).length;
    
    // Calculate total revenue from paid invoices
    const paidInvoices = invoices.filter((i: any) => i.status === "paid");
    const totalRevenue = paidInvoices.reduce((sum: number, inv: any) => {
      // Calculate total from items and adjustments
      const items = (inv.invoice_items || []) as any[];
      const adjustments = (inv.invoice_adjustments || []) as any[];
      const subtotal = items.reduce((s: number, it: any) => s + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
      const taxes = adjustments
        .filter((a: any) => a.type === "tax" && !a.included_in_prices)
        .reduce((s: number, a: any) => {
          const mode = a.mode || "fixed";
          const rate = Number(a.percent_rate || 0);
          const fixed = Number(a.amount || 0);
          if (mode === "percent") {
            return s + (subtotal * (rate / 100));
          }
          return s + fixed;
        }, 0);
      const discounts = adjustments
        .filter((a: any) => a.type === "discount")
        .reduce((s: number, a: any) => {
          const mode = a.mode || "fixed";
          const rate = Number(a.percent_rate || 0);
          const fixed = Number(a.amount || 0);
          if (mode === "percent") {
            return s + (subtotal * (rate / 100));
          }
          return s + fixed;
        }, 0);
      return sum + (subtotal + taxes - discounts);
    }, 0);

    return { total, paid, pending, overdue, totalRevenue };
  }, [invoices]);

  const totals = useMemo(() => {
    const subtotal = items.reduce((sum, it) => sum + Number(it.quantity || 0) * Number(it.unit_price || 0), 0);
    const taxes = adjustments
      .filter((a) => a.type === "tax" && !a.included_in_prices)
      .reduce((sum, a) => sum + computeAdjustmentAmount(a, subtotal), 0);
    const discounts = adjustments
      .filter((a) => a.type === "discount")
      .reduce((sum, a) => sum + computeAdjustmentAmount(a, subtotal), 0);
    const total = subtotal + taxes - discounts;
    return { subtotal, taxes, discounts, total };
  }, [items, adjustments]);

  const fetchInvoices = async () => {
    if (!business?.id) return;
    setLoading(true);
    try {
      const { data, error } = await sb
        .from("invoices")
        .select("*, invoice_items(*), invoice_adjustments(*)")
        .eq("business_id", business.id)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      setInvoices(data || []);
    } catch (e: any) {
      toast({ title: "Failed to load invoices", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  // Support deep-linking into edit mode: /app/invoicing?edit=<invoiceId>
  useEffect(() => {
    if (!invoices?.length) return;
    const sp = new URLSearchParams(location.search || "");
    const editId = sp.get("edit");
    if (!editId) return;
    if (handledEditParamRef.current === editId) return;

    const inv = invoices.find((i: any) => String(i.id) === String(editId));
    if (!inv) return;
    handledEditParamRef.current = editId;
    openEdit(inv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, invoices]);

  const reset = () => {
    setEditing(null);
    setItems([{ description: "", quantity: "1", unit_price: "0" }]);
    setAdjustments([]);
    setSelectedCustomer(null);
    setCustomerId(null);
    setCustomerQuery("");
    setCustomerResults([]);
    setForm({
      bill_to_name: "",
      bill_to_email: "",
      bill_to_phone: "",
      issue_date: format(new Date(), "yyyy-MM-dd"),
      due_date: "",
      payment_terms: "",
      notes: "",
      terms: "",
      status: "draft",
    });
    lastCreatedIdRef.current = null;
  };

  const openCreate = () => {
    reset();
    setOpen(true);
  };

  const openEdit = async (inv: any) => {
    setEditing(inv);
    lastCreatedIdRef.current = inv.id;
    setCustomerId(inv.customer_id || null);
    setSelectedCustomer(null);
    setForm({
      bill_to_name: inv.bill_to_name || "",
      bill_to_email: inv.bill_to_email || "",
      bill_to_phone: inv.bill_to_phone || "",
      issue_date: inv.issue_date || format(new Date(), "yyyy-MM-dd"),
      due_date: inv.due_date || "",
      payment_terms: inv.payment_terms || "",
      notes: inv.notes || "",
      terms: inv.terms || "",
      status: inv.status || "draft",
    });
    const { data } = await sb
      .from("invoice_items")
      .select("description,quantity,unit_price,position")
      .eq("invoice_id", inv.id)
      .order("position", { ascending: true });
    setItems((data || []).map((r: any) => ({ description: r.description, quantity: String(r.quantity), unit_price: String(r.unit_price) })));
    const subtotalLocal = (data || []).reduce(
      (sum: number, r: any) => sum + Number(r.quantity || 0) * Number(r.unit_price || 0),
      0,
    );

    const { data: adj } = await sb
      .from("invoice_adjustments")
      .select("type,label,amount,mode,percent_rate,included_in_prices,position")
      .eq("invoice_id", inv.id)
      .order("position", { ascending: true });
    setAdjustments(
      (adj || []).map((r: any) => ({
        type: r.type,
        label: r.label,
        // UI is percent-only. If an older invoice used fixed amount, we convert it to an equivalent percent.
        percent_rate:
          r.mode === "fixed" && Number(r.amount || 0) !== 0
            ? String(subtotalLocal > 0 ? (Number(r.amount || 0) / subtotalLocal) * 100 : 0)
            : String(r.percent_rate ?? ""),
        included_in_prices: Boolean(r.included_in_prices),
      })),
    );
    // try load customer info for display
    if (inv.customer_id) {
      const { data: c } = await sb.from("customers").select("id,name,email,phone").eq("id", inv.customer_id).maybeSingle();
      if (c) setSelectedCustomer(c);
    }
    setOpen(true);
  };

  const searchCustomers = async () => {
    if (!business?.id) return;
    const q = customerQuery.trim();
    if (!q) return;
    setCustomerSearching(true);
    try {
      const { data, error } = await sb
        .from("customers")
        .select("id,name,email,phone")
        .eq("business_id", business.id)
        .or(`name.ilike.%${q}%,email.ilike.%${q}%,phone.ilike.%${q}%`)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) throw error;
      setCustomerResults(data || []);
    } catch (e: any) {
      toast({ title: "Customer search failed", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setCustomerSearching(false);
    }
  };

  const selectCustomer = async (c: any) => {
    setSelectedCustomer(c);
    setCustomerId(c.id);
    setForm((f) => ({
      ...f,
      bill_to_name: c.name || f.bill_to_name,
      bill_to_email: c.email || f.bill_to_email,
      bill_to_phone: c.phone || f.bill_to_phone,
    }));

    // Autofill membership line item if customer has an active membership
    try {
      const { data: mem } = await sb
        .from("memberships")
        .select("id,expires_at,status,plan:membership_plans(id,name,price_amount,currency)")
        .eq("business_id", business?.id)
        .eq("customer_id", c.id)
        .order("expires_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      const plan = mem?.plan;
      const isActive = mem && mem.status === "active" && mem.expires_at && new Date(mem.expires_at) >= new Date();
      if (isActive && plan?.price_amount != null) {
        setItems([
          {
            description: `Membership • ${plan.name}`,
            quantity: "1",
            unit_price: String(plan.price_amount),
          },
        ]);
        toast({ title: "Prefilled from membership", description: "You can edit the line item if needed." });
      }
    } catch {
      // no-op
    }

    setCustomerOpen(false);
  };

  const addLine = () => setItems((arr) => [...arr, { description: "", quantity: "1", unit_price: "0" }]);
  const removeLine = (idx: number) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const addAdjustment = (type: "tax" | "discount") =>
    setAdjustments((arr) => [
      ...arr,
      {
        type,
        label: type === "tax" ? "VAT" : "Discount",
        percent_rate: type === "tax" ? "15" : "0",
        included_in_prices: false,
      },
    ]);
  const removeAdjustment = (idx: number) => setAdjustments((arr) => arr.filter((_, i) => i !== idx));

  const save = async () => {
    if (!business?.id) return;
    if (!form.bill_to_name.trim()) {
      toast({ title: "Bill To is required", description: "Enter the client/business name.", variant: "destructive" });
      return;
    }
    const cleanItems = items
      .map((it) => ({
        description: it.description.trim(),
        quantity: Number(it.quantity || 0),
        unit_price: Number(it.unit_price || 0),
      }))
      .filter((it) => it.description && it.quantity > 0);
    if (cleanItems.length === 0) {
      toast({ title: "Add at least one item", description: "Your invoice needs line items.", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      const payload: any = {
        business_id: business.id,
        customer_id: customerId,
        status: form.status,
        currency,
        issue_date: form.issue_date,
        due_date: form.due_date || null,
        payment_terms: form.payment_terms || null,
        bill_to_name: form.bill_to_name.trim(),
        bill_to_email: form.bill_to_email.trim() || null,
        bill_to_phone: form.bill_to_phone.trim() || null,
        notes: form.notes || null,
        terms: form.terms || null,
      };

      let invoiceId = editing?.id as string | undefined;
      if (invoiceId) {
        const { error } = await sb.from("invoices").update(payload).eq("id", invoiceId);
        if (error) throw error;
        await sb.from("invoice_items").delete().eq("invoice_id", invoiceId);
      } else {
        const { data: created, error } = await sb.from("invoices").insert(payload).select("*").single();
        if (error) throw error;
        invoiceId = created.id;
        lastCreatedIdRef.current = invoiceId;
      }

      const itemsPayload = cleanItems.map((it, idx) => ({
        business_id: business.id,
        invoice_id: invoiceId,
        position: idx + 1,
        description: it.description,
        quantity: it.quantity,
        unit_price: it.unit_price,
      }));
      const { error: iErr } = await sb.from("invoice_items").insert(itemsPayload);
      if (iErr) throw iErr;

      // Save taxes/discounts
      const cleanAdj = adjustments
        .map((a) => ({
          type: a.type,
          label: a.label.trim(),
          mode: "percent",
          amount: 0,
          percent_rate: Number(a.percent_rate || 0),
          included_in_prices: a.type === "tax" ? Boolean(a.included_in_prices) : false,
        }))
        .filter((a) => {
          if (!a.label) return false;
          return Number(a.percent_rate || 0) !== 0;
        });

      await sb.from("invoice_adjustments").delete().eq("invoice_id", invoiceId);
      if (cleanAdj.length > 0) {
        const adjPayload = cleanAdj.map((a, idx) => ({
          business_id: business.id,
          invoice_id: invoiceId,
          type: a.type,
          label: a.label,
          amount: a.amount,
          mode: a.mode,
          percent_rate: a.percent_rate,
          included_in_prices: a.included_in_prices,
          position: idx + 1,
        }));
        const { error: aErr } = await sb.from("invoice_adjustments").insert(adjPayload);
        if (aErr) throw aErr;
      }

      toast({ title: "Invoice saved" });
      setOpen(false);
      reset();
      await fetchInvoices();
    } catch (e: any) {
      toast({ title: "Save failed", description: e?.message || "Try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const exportCSV = () => {
    const rows = filtered.map((inv: any) => ({
      invoice_number: inv.invoice_number,
      status: inv.status,
      bill_to_name: inv.bill_to_name,
      bill_to_email: inv.bill_to_email || "",
      issue_date: inv.issue_date,
      due_date: inv.due_date || "",
      currency: inv.currency,
    }));
    if (!rows.length) return;
    const headers = Object.keys(rows[0]);
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [headers.join(","), ...rows.map((r: any) => headers.map((h) => esc((r as any)[h])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoices_${format(new Date(), "yyyyMMdd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const sendInvoice = async (inv: any) => {
    if (!inv?.id) return;
    if (!inv.bill_to_email) {
      toast({ title: "Missing email", description: "Add a Bill To email to send.", variant: "destructive" });
      return;
    }
    try {
      const { error } = await supabase.functions.invoke("send-email", {
        body: { type: "invoice", invoiceId: inv.id },
      });
      if (error) throw error;
      const { error: upErr } = await sb
        .from("invoices")
        .update({ status: "sent", sent_at: new Date().toISOString() })
        .eq("id", inv.id);
      if (upErr) throw upErr;
      toast({ title: "Invoice sent" });
      await fetchInvoices();
    } catch (e: any) {
      let details = e?.message || "Try again.";
      // Supabase functions errors often include a Response in `context`
      try {
        const res = e?.context;
        if (res && typeof res.text === "function") {
          const bodyText = await res.text();
          const status = res.status ? `HTTP ${res.status}` : "HTTP error";
          details = bodyText ? `${status}: ${bodyText}` : status;
        }
      } catch {
        // ignore
      }
      toast({ title: "Failed to send", description: details, variant: "destructive" });
    }
  };

  return (
    <AppShell businessName={business?.name || "My Business"}>
      <PageHeader
        title="Invoicing"
        description="Create and send invoices to business clients"
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={exportCSV} disabled={filtered.length === 0}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
            <Button size="pill" onClick={openCreate} disabled={businessLoading}>
              <Plus className="mr-2 h-4 w-4" />
              New Invoice
            </Button>
          </div>
        }
      />

      <div className="mb-6">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search invoices…" className="pl-9" />
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard
          title="Total Invoices"
          description="All invoices"
          value={invoiceMetrics.total.toString()}
          icon={FileText}
        />
        <StatCard
          title="Paid"
          description="Successfully paid"
          value={invoiceMetrics.paid.toString()}
          icon={CheckCircle2}
        />
        <StatCard
          title="Pending"
          description="Awaiting payment"
          value={invoiceMetrics.pending.toString()}
          icon={Clock}
        />
        <StatCard
          title="Overdue"
          description="Past due date"
          value={invoiceMetrics.overdue.toString()}
          icon={CalendarIcon}
        />
        <StatCard
          title="Total Revenue"
          description="From paid invoices"
          value={money(currency, invoiceMetrics.totalRevenue)}
          icon={Banknote}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground">
          No invoices yet. Create one to get started.
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice</TableHead>
                <TableHead>Bill To</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Issue</TableHead>
                <TableHead>Due</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((inv: any) => (
                <TableRow key={inv.id}>
                  <TableCell className="font-medium">
                    <div className="font-mono text-xs">{inv.invoice_number}</div>
                    <div className="text-xs text-muted-foreground">{inv.currency}</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{inv.bill_to_name}</div>
                    <div className="text-xs text-muted-foreground">{inv.bill_to_email || "—"}</div>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={inv.status === "paid" ? "default" : "secondary"} 
                      className={inv.status === "paid" ? "bg-success-bg text-success border-success/20 capitalize" : "capitalize"}
                    >
                      {inv.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{inv.issue_date}</TableCell>
                  <TableCell className="text-sm">{inv.due_date || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => openEdit(inv)}
                        disabled={inv.status === "paid"}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`${getBaseUrl()}/app/invoicing/preview/${inv.id}`, "_blank")}
                      >
                        <ExternalLink className="mr-2 h-4 w-4" />
                        Preview
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={() => sendInvoice(inv)}
                        disabled={inv.status === "paid"}
                      >
                        <Send className="mr-2 h-4 w-4" />
                        {inv.status === "sent" ? "Resend" : "Send"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) reset();
        }}
      >
        <DialogContent className="sm:max-w-md w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>{editing ? "Edit invoice" : "New invoice"}</DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0 space-y-4">
            <div className="rounded-lg border border-border bg-accent/30 p-3 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">Customer</p>
                <p className="text-sm font-medium text-foreground truncate">
                  {selectedCustomer?.name || "Not selected"}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {selectedCustomer?.email || selectedCustomer?.phone || ""}
                </p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={() => setCustomerOpen(true)}>
                Select
              </Button>
            </div>

            <div className="grid gap-2">
              <Label>Bill to (business)</Label>
              <Input value={form.bill_to_name} onChange={(e) => setForm((f) => ({ ...f, bill_to_name: e.target.value }))} placeholder="Company name" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>Email</Label>
                <Input value={form.bill_to_email} onChange={(e) => setForm((f) => ({ ...f, bill_to_email: e.target.value }))} placeholder="accounts@company.com" />
              </div>
              <div className="grid gap-2">
                <Label>Phone</Label>
                <Input value={form.bill_to_phone} onChange={(e) => setForm((f) => ({ ...f, bill_to_phone: e.target.value }))} placeholder="+233…" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="grid gap-2">
                <Label>Issue date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between">
                      <span className={form.issue_date ? "text-foreground" : "text-muted-foreground"}>
                        {form.issue_date ? format(parseISO(form.issue_date), "MM/dd/yyyy") : "Select date"}
                      </span>
                      <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.issue_date ? parseISO(form.issue_date) : undefined}
                      onSelect={(d) => {
                        if (!d) return;
                        setForm((f) => ({ ...f, issue_date: format(d, "yyyy-MM-dd") }));
                      }}
                      initialFocus
                      className="p-2"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid gap-2">
                <Label>Due date (optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between">
                      <span className={form.due_date ? "text-foreground" : "text-muted-foreground"}>
                        {form.due_date ? format(parseISO(form.due_date), "MM/dd/yyyy") : "Select date"}
                      </span>
                      <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.due_date ? parseISO(form.due_date) : undefined}
                      onSelect={(d) => {
                        if (!d) {
                          setForm((f) => ({ ...f, due_date: "" }));
                          return;
                        }
                        setForm((f) => ({ ...f, due_date: format(d, "yyyy-MM-dd") }));
                      }}
                      initialFocus
                      className="p-2"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Payment terms (optional)</Label>
              <Input value={form.payment_terms} onChange={(e) => setForm((f) => ({ ...f, payment_terms: e.target.value }))} placeholder="e.g. Net 7" />
            </div>

            <Card className="rounded-2xl">
              <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">Line items</p>
                <Button size="sm" variant="outline" onClick={addLine}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add item
                </Button>
              </div>
              <div className="mt-3 space-y-3">
                {items.map((it, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-12">
                      <Input
                        value={it.description}
                        onChange={(e) =>
                          setItems((arr) => arr.map((x, i) => (i === idx ? { ...x, description: e.target.value } : x)))
                        }
                        placeholder="Description"
                      />
                    </div>
                    <div className="col-span-4">
                      <Input
                        type="number"
                        value={it.quantity}
                        onChange={(e) =>
                          setItems((arr) => arr.map((x, i) => (i === idx ? { ...x, quantity: e.target.value } : x)))
                        }
                        placeholder="Qty"
                      />
                    </div>
                    <div className="col-span-6">
                      <Input
                        type="number"
                        value={it.unit_price}
                        onChange={(e) =>
                          setItems((arr) => arr.map((x, i) => (i === idx ? { ...x, unit_price: e.target.value } : x)))
                        }
                        placeholder={`Rate (${currency})`}
                      />
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <Button size="icon" variant="ghost" onClick={() => removeLine(idx)} disabled={items.length === 1}>
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                    <div className="col-span-12 text-xs text-muted-foreground text-right">
                      Line total:{" "}
                      {money(currency, Number(it.quantity || 0) * Number(it.unit_price || 0))}
                    </div>
                  </div>
                ))}
                <div className="pt-2 border-t border-border space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Subtotal</span>
                    <span className="text-sm font-semibold text-foreground">{money(currency, totals.subtotal)}</span>
                  </div>
                  {totals.taxes !== 0 ? (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Taxes</span>
                      <span className="text-sm font-semibold text-foreground">{money(currency, totals.taxes)}</span>
                    </div>
                  ) : null}
                  {totals.discounts !== 0 ? (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Discounts</span>
                      <span className="text-sm font-semibold text-foreground">-{money(currency, totals.discounts)}</span>
                    </div>
                  ) : null}
                  <div className="flex items-center justify-between pt-2 border-t border-border">
                    <span className="text-sm text-muted-foreground">Total</span>
                    <span className="text-sm font-semibold text-foreground">{money(currency, totals.total)}</span>
                  </div>
                </div>
              </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Taxes & Discounts</p>
                    <p className="text-xs text-muted-foreground">Simple percentage-based adjustments.</p>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => addAdjustment("tax")}>
                      + Tax
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => addAdjustment("discount")}>
                      + Discount
                    </Button>
                  </div>
                </div>

                {adjustments.length === 0 ? (
                  <p className="text-sm text-muted-foreground mt-3">No taxes/discounts added.</p>
                ) : (
                  <div className="mt-3 space-y-3">
                    {adjustments.map((a, idx) => {
                      const computed = computeAdjustmentAmount(a, totals.subtotal);
                      return (
                        <div key={idx} className="rounded-xl border border-border bg-background p-3">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <Badge variant="secondary" className="capitalize">
                                {a.type === "tax" ? "Tax" : "Discount"}
                              </Badge>
                              <div className="text-xs text-muted-foreground truncate">{a.type === "tax" && a.included_in_prices ? "Included in prices" : "Applies to total"}</div>
                            </div>
                            <Button type="button" size="sm" variant="ghost" onClick={() => removeAdjustment(idx)}>
                              Remove
                            </Button>
                          </div>

                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-12 gap-2 items-start">
                            <div className="sm:col-span-8">
                              <Label className="text-xs text-muted-foreground">Label</Label>
                              <Input
                                className="mt-1"
                                value={a.label}
                                onChange={(e) =>
                                  setAdjustments((arr) =>
                                    arr.map((x, i) => (i === idx ? { ...x, label: e.target.value } : x)),
                                  )
                                }
                                placeholder={a.type === "tax" ? "VAT" : "Withholding tax"}
                              />
                              {a.type === "tax" ? (
                                <div className="mt-2 flex items-center justify-between gap-3">
                                  <div className="text-xs text-muted-foreground">Included in price</div>
                                  <Switch
                                    checked={a.included_in_prices}
                                    onCheckedChange={(v) =>
                                      setAdjustments((arr) =>
                                        arr.map((x, i) => (i === idx ? { ...x, included_in_prices: Boolean(v) } : x)),
                                      )
                                    }
                                  />
                                </div>
                              ) : null}
                            </div>

                            <div className="sm:col-span-4">
                              <Label className="text-xs text-muted-foreground">Rate (%)</Label>
                              <div className="mt-1 flex items-center gap-2">
                                <Input
                                  type="number"
                                  value={a.percent_rate}
                                  onChange={(e) =>
                                    setAdjustments((arr) =>
                                      arr.map((x, i) => (i === idx ? { ...x, percent_rate: e.target.value } : x)),
                                    )
                                  }
                                  placeholder="0"
                                />
                                <span className="text-sm text-muted-foreground">%</span>
                              </div>
                              <div className="mt-1 text-[11px] text-muted-foreground text-right">
                                {a.type === "discount" ? "-" : ""}≈ {money(currency, computed)}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="grid gap-2">
              <Label>Notes (optional)</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className="min-h-[80px]" />
            </div>
            <div className="grid gap-2">
              <Label>Terms (optional)</Label>
              <Textarea value={form.terms} onChange={(e) => setForm((f) => ({ ...f, terms: e.target.value }))} className="min-h-[80px]" />
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-border bg-background flex-row gap-3 flex-shrink-0">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving} className="flex-1 rounded-lg">
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="flex-1 rounded-lg">
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={customerOpen} onOpenChange={setCustomerOpen}>
        <DialogContent className="sm:max-w-md w-[calc(100%-2rem)] max-h-[90vh] p-0 gap-0 overflow-hidden rounded-2xl flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogTitle>Select customer</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-4 flex-1 min-h-0">
            <div className="space-y-3">
              <div className="flex gap-2">
              <Input
                value={customerQuery}
                onChange={(e) => setCustomerQuery(e.target.value)}
                placeholder="Search name, email, phone…"
                onKeyDown={(e) => {
                  if (e.key === "Enter") searchCustomers();
                }}
              />
              <Button variant="outline" onClick={searchCustomers} disabled={customerSearching || !customerQuery.trim()}>
                {customerSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
              </Button>
              </div>

              {customerResults.length === 0 ? (
                <p className="text-sm text-muted-foreground">Search to find an existing customer.</p>
              ) : (
                <div className="max-h-64 overflow-y-auto rounded-lg border border-border bg-background">
                  {customerResults.map((c: any) => (
                    <button
                      key={c.id}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-accent/50 border-b border-border last:border-b-0"
                      onClick={() => selectCustomer(c)}
                    >
                      <p className="text-sm font-medium text-foreground">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.email || "—"} • {c.phone || "—"}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}


